import React, {useContext, useState} from "react";
import "./Reviews.css";
import {reviewsSection} from "../../portfolio";
import {Fade} from "react-reveal";
import StyleContext from "../../contexts/StyleContext";

export default function Reviews() {
  const {isDark} = useContext(StyleContext);
  const [expanded, setExpanded] = useState(false);

  if (!reviewsSection.display) {
    return null;
  }

  const defaultReviewCount = 4;

  return (
    <Fade bottom duration={1000} distance="20px">
      <div
        className="reviews-main py-20 px-4 md:px-10 lg:px-20 max-w-7xl mx-auto flex flex-col lg:flex-row gap-10 items-center justify-between"
        id="reviews"
      >
        {/* Left Side: Masonry Grid of Reviews */}
        <div className="w-full lg:w-3/5 grid gap-4 grid-cols-1 md:grid-cols-2 mt-10 items-start">
          {reviewsSection.reviews.map((review, i) => {
            const isDefault = i < defaultReviewCount;
            const content = (
              <div
                className={`p-6 rounded-2xl flex flex-col gap-4 border border-[#ffffff10] backdrop-blur-md transition-all duration-300 hover:-translate-y-1 ${
                  isDark
                    ? "bg-[#0A0A0A]/80 text-white"
                    : "bg-white/80 shadow-lg text-black"
                }`}
                style={{
                  marginTop: i % 2 !== 0 ? "2rem" : "0" // simple staggered layout
                }}
              >
                {/* Header: Avatar, Name, Handle */}
                <div className="flex items-center gap-3">
                  <img
                    src={review.avatar}
                    alt={review.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="font-bold text-sm tracking-wide">
                      {review.name}
                    </span>
                    <span className="text-xs opacity-60 m-0">
                      {review.username}
                    </span>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex text-[#f5a623]">
                  {/* 5 stars, gold color */}
                  {[...Array(review.rating)].map((_, index) => (
                    <svg
                      key={index}
                      className="w-4 h-4 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                {/* Text */}
                <p className="opacity-80 text-sm leading-relaxed m-0 text-left">
                  {review.text}
                </p>
              </div>
            );

            return isDefault ? (
              <React.Fragment key={i}>{content}</React.Fragment>
            ) : (
              <Fade bottom collapse when={expanded} duration={400} key={i}>
                {content}
              </Fade>
            );
          })}
        </div>

        {/* Right Side: Title, Subtitle, CTA */}
        <div className="w-full lg:w-2/5 flex flex-col items-start text-left xl:pl-10">
          <h1
            className={`text-5xl font-extrabold leading-tight tracking-tight mb-4 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {reviewsSection.title.split(" ").map((word, i) => {
              // Accentuating the word "Say" with green or highlighting specific words
              if (
                word.toLowerCase() === "say" ||
                word.toLowerCase() === "customers"
              ) {
                return (
                  <span key={i} className="text-[#f5a623]">
                    {" "}
                    {word}
                  </span>
                );
              }
              return ` ${word}`;
            })}
          </h1>
          <p
            className={`text-lg mb-8 opacity-70 ${
              isDark ? "text-white" : "text-gray-800"
            }`}
          >
            {reviewsSection.subtitle}
          </p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="group flex flex-row items-center justify-center gap-3 w-full sm:w-auto px-8 py-3 rounded-lg border border-[#f5a623]/50 bg-transparent hover:bg-[#f5a623]/10 transition-colors duration-300"
          >
            {!expanded ? (
              <svg
                className="w-4 h-4 text-[#f5a623] fill-current"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ) : (
              <svg
                className="w-4 h-4 text-[#f5a623] fill-current"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L10 4.414 4.707 9.707a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span
              className={`font-semibold ${
                isDark ? "text-white" : "text-black"
              }`}
            >
              {expanded ? "Show Less" : "View All Reviews"}
            </span>
            <span
              className={`transition-transform duration-300 ${
                expanded
                  ? "group-hover:-translate-y-1"
                  : "group-hover:translate-x-1"
              } ${isDark ? "text-white" : "text-black"}`}
            >
              {expanded ? "↑" : "→"}
            </span>
          </button>
        </div>
      </div>
    </Fade>
  );
}
